import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';

import { brand, destructive, radius } from 'utils/designTokens';

const TOAST_DURATION_MS = 3000;

const ToastContext = createContext(null);

/** Turn harsh SQL/API errors into short, user-friendly text when possible */
export function humanizeErrorMessage(raw) {
  if (raw == null || String(raw).trim() === '') return 'Something went wrong.';
  const s = String(raw);
  if (/foreign key constraint|Cannot delete or update a parent row|REFERENCES `/i.test(s)) {
    return "This can't be removed because it's still linked to other records (e.g. files or images). Remove related items first, or try again after updating dependencies.";
  }
  if (/ER_NO_REFERENCED_ROW|1451|1452/i.test(s)) {
    return 'This action conflicts with related data in the system. Please refresh and try again, or contact support.';
  }
  return s;
}

const ACCENTS = {
  error: { border: destructive.main, icon: destructive.main },
  success: { border: brand.primary, icon: brand.primary },
  warning: { border: '#e65100', icon: '#e65100' },
  info: { border: brand.primary, icon: brand.primary }
};

/** Extract message from axios-style errors */
export function getApiErrorMessage(err, fallback = 'Something went wrong.') {
  const msg = err?.response?.data?.message ?? err?.message;
  return msg != null && String(msg).trim() !== '' ? String(msg) : fallback;
}

/** Card-style toast: white surface, left accent bar — matches MainCard / dialogs */
const alertSx = (severity) => {
  const a = ACCENTS[severity] || ACCENTS.error;
  return {
    width: '100%',
    maxWidth: { xs: 'min(100%, 400px)', sm: 440 },
    alignItems: 'flex-start',
    py: 1.5,
    px: 1,
    borderRadius: `${radius.md}px`,
    bgcolor: '#ffffff',
    color: 'text.primary',
    border: `1px solid ${brand.border}`,
    borderLeftWidth: 5,
    borderLeftColor: a.border,
    boxShadow: brand.shadowLg,
    backgroundImage: 'none',
    '& .MuiAlert-icon': {
      color: a.icon,
      mt: 0.15,
      opacity: 1,
      alignSelf: 'flex-start'
    },
    '& .MuiAlert-message': {
      minWidth: 0,
      maxHeight: 200,
      overflow: 'auto',
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap',
      fontSize: '0.875rem',
      lineHeight: 1.55,
      fontWeight: 500,
      letterSpacing: '0.01em',
      color: 'rgba(15, 45, 32, 0.88)',
      pr: 0.5
    },
    '& .MuiAlert-action': {
      display: 'none',
      width: 0,
      m: 0,
      p: 0
    }
  };
};

export function ToastProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    message: '',
    severity: 'error'
  });

  const showToast = useCallback((message, options = {}) => {
    let text =
      message != null && String(message).trim() !== '' ? String(message) : 'Something went wrong.';
    const allowed = ['error', 'success', 'warning', 'info'];
    const severity = allowed.includes(options.severity) ? options.severity : 'error';
    if (severity === 'error' && options.humanize !== false) {
      text = humanizeErrorMessage(text);
    }
    setState({
      open: true,
      message: text,
      severity
    });
  }, []);

  const hideToast = useCallback((_, reason) => {
    if (reason === 'clickaway') return;
    setState((s) => ({ ...s, open: false }));
  }, []);

  const showApiError = useCallback(
    (err, fallback = 'Something went wrong.', options = {}) => {
      showToast(getApiErrorMessage(err, fallback), { severity: 'error', ...options });
    },
    [showToast]
  );

  const value = useMemo(() => ({ showToast, showApiError }), [showToast, showApiError]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={TOAST_DURATION_MS}
        onClose={hideToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          zIndex: (theme) => theme.zIndex.snackbar,
          px: { xs: 2, sm: 0 },
          pb: { xs: 2, sm: 3 }
        }}
      >
        <Alert severity={state.severity} variant="standard" sx={alertSx(state.severity)}>
          {state.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

ToastProvider.propTypes = { children: PropTypes.node };

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}
